import type { Query, QueryCache, QueryClient } from "@tanstack/react-query";

/**
 * Re-read the answers that predate a change, once each, without cancelling
 * anything. Capacity is the reader (NG-2812).
 *
 * Three signals report one capacity write: the writer's own refresh, the write
 * listener (`schedule-invalidation.ts`) and, seconds later, the backend's
 * `capacity:list` hint once the rebuild lands. With plain `invalidateQueries`
 * each one cancelled the reads the one before had started and asked again, so
 * one reassign on the box showed every query on screen read three or four times
 * with cancelled rows between (the PR-7210 box test).
 *
 * So each signal carries the moment it describes, and this re-reads only the
 * answers read before it. "Read" means when the request started, not when it
 * landed: a read that started before a rebuild and landed after it still
 * carries the old rows. A query already being read since then is left to
 * finish. A read from before is left to finish too, and asked again after it.
 * Nothing is cancelled. A query off screen is only marked stale, so its next
 * visit asks again.
 */

/** When the read behind each query's current answer started, and when its pending read did. */
const answeredAt = new WeakMap<Query, number>();
const pendingAt = new WeakMap<Query, number>();
const tracked = new WeakSet<QueryCache>();

/**
 * Record when each read starts, from the client's cache events. Idempotent per
 * cache. `createQueryClient` installs it, so every read the app makes is timed.
 */
export function trackReadStarts(client: QueryClient): void {
  const cache = client.getQueryCache();
  if (tracked.has(cache)) return;
  tracked.add(cache);
  cache.subscribe((event) => {
    if (event.type !== "updated") return;
    const { query, action } = event;
    if (action.type === "fetch") {
      pendingAt.set(query, Date.now());
    } else if (action.type === "success" && !action.manual) {
      // An optimistic `setQueryData` is `manual`: not a read, so it leaves the time alone.
      const started = pendingAt.get(query);
      if (started !== undefined) answeredAt.set(query, started);
      pendingAt.delete(query);
    } else if (action.type === "error") {
      pendingAt.delete(query);
    }
  });
}

function isFetching(query: Query): boolean {
  return query.state.fetchStatus !== "idle";
}

/** True when `query` holds, or is reading, an answer asked for at or after `since`. */
function readSince(query: Query, since: number): boolean {
  if (isFetching(query)) return (pendingAt.get(query) ?? Number.NEGATIVE_INFINITY) >= since;
  // An answer no tracked read produced (seeded with `setQueryData`, or read
  // before tracking began) counts as older: re-reading it costs a request,
  // keeping it could cost the user the change.
  return (answeredAt.get(query) ?? Number.NEGATIVE_INFINITY) >= since;
}

function refetchOne(client: QueryClient, query: Query): Promise<void> {
  return client.refetchQueries(
    { predicate: (candidate) => candidate === query, type: "active" },
    { cancelRefetch: false },
  );
}

/**
 * Re-read every query `matches` picks whose answer was asked for before
 * `since`, and mark it stale. Resolves once the re-reads have settled.
 */
export async function rereadOlderThan(
  client: QueryClient,
  matches: (query: Query) => boolean,
  since: number,
): Promise<void> {
  trackReadStarts(client);
  const older = client
    .getQueryCache()
    .findAll({ predicate: (query) => matches(query) && !readSince(query, since) });
  if (older.length === 0) return;
  const olderSet = new Set<Query>(older);
  // An older read in flight is left to land: `cancelRefetch: false` makes the
  // refetch below join it rather than cancel it. Ask again once it has.
  const inFlight = older.filter(isFetching);
  await Promise.all([
    client.invalidateQueries(
      { predicate: (query) => olderSet.has(query) },
      { cancelRefetch: false },
    ),
    ...inFlight.map(async (query) => {
      await query.promise?.catch(() => undefined);
      if (query.isActive() && !readSince(query, since)) await refetchOne(client, query);
    }),
  ]);
}
